import React, { useCallback, useState } from 'react';
import { HoverTooltip } from '../../components/common/HoverTooltip';

const hasText = (value) => !!(value && String(value).trim());

/** 「キャンセル・返品・不具合」で「選択してください」以外が選ばれているか */
export const hasCancelReturnIssueSelected = (value) => hasText(value);

const BADGE_DEFS = [
  {
    key: 'adjustment',
    label: '調',
    fieldLabel: '調整メモ',
    getBody: (row) => row.adjustment_memo,
    show: (row) => hasText(row.adjustment_memo),
    variant: 'default',
  },
  {
    key: 'comment',
    label: 'コ',
    fieldLabel: 'コメント',
    getBody: (row) => row.comment,
    show: (row) => hasText(row.comment),
    variant: 'default',
  },
  {
    key: 'shipping',
    label: '送',
    fieldLabel: '送料メモ',
    getBody: (row) => row.shipping_memo,
    show: (row) => hasText(row.shipping_memo),
    variant: 'default',
  },
  {
    key: 'cancel_return_issue',
    label: '不',
    fieldLabel: 'キャンセル・返品・不具合',
    getBody: (row) => row.cancel_return_issue,
    show: (row) => hasCancelReturnIssueSelected(row.cancel_return_issue),
    variant: 'issue',
  },
];

/** バッジキーに対応するツールチップ内容を組み立てる */
export function buildMemoBadgeTooltip(badgeKey, row) {
  const def = BADGE_DEFS.find((item) => item.key === badgeKey);
  if (!def || !row) return null;
  const body = def.getBody(row);
  if (!hasText(body)) return null;
  return {
    label: def.fieldLabel,
    body: String(body).trim(),
  };
}

const BADGE_VARIANT_CLASS = {
  default: 'border-slate-400 text-slate-700 bg-white',
  issue: 'border-red-300 text-red-400 bg-white',
};

/**
 * 発送予定一覧の「メモ」欄バッジ（調・コ・送・不）。
 * row: ShipmentPlanItemResponse 相当のオブジェクト
 */
export const ShipmentMemoBadges = ({ row }) => {
  const [tooltip, setTooltip] = useState({ show: false, label: '', body: '', x: 0, y: 0 });

  const hideTooltip = useCallback(() => {
    setTooltip({ show: false, label: '', body: '', x: 0, y: 0 });
  }, []);

  const showTooltipForBadge = useCallback((e, badgeKey) => {
    e.stopPropagation();
    const content = buildMemoBadgeTooltip(badgeKey, row);
    if (!content) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      show: true,
      label: content.label,
      body: content.body,
      x: rect.left + rect.width / 2,
      y: rect.top - 8,
    });
  }, [row]);

  const badges = BADGE_DEFS.filter((def) => def.show(row));

  if (badges.length === 0) {
    return '-';
  }

  return (
    <>
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        {badges.map((badge) => {
          const content = buildMemoBadgeTooltip(badge.key, row);
          const ariaLabel = content ? `${content.label}: ${content.body}` : badge.label;
          return (
            <span
              key={badge.key}
              aria-label={ariaLabel}
              onMouseEnter={(e) => showTooltipForBadge(e, badge.key)}
              onMouseLeave={(e) => {
                e.stopPropagation();
                hideTooltip();
              }}
              className={`inline-flex h-6 w-6 items-center justify-center rounded-full border text-[11px] font-semibold cursor-default ${
                BADGE_VARIANT_CLASS[badge.variant] || BADGE_VARIANT_CLASS.default
              }`}
            >
              {badge.label}
            </span>
          );
        })}
      </div>
      <HoverTooltip
        show={tooltip.show}
        x={tooltip.x}
        y={tooltip.y}
        label={tooltip.label}
        body={tooltip.body}
      />
    </>
  );
};
